import React from 'react';
import Link from 'components/commercetools-ui/atoms/link';
import Typography from 'components/commercetools-ui/atoms/typography';
import Image from 'frontastic/lib/image';
import { NationalBook } from 'fonts';
import { GenericSectionContent } from '../../types';
import { useAdditionalCss } from '../../../helpers/useAdditionalCss';
import { textAlignmentClass, sanitizeHtml, SECTION_SPACING } from '../../../helpers/rendering';

/**
 * Quasar — the technology grid from /our-technologies/.
 *
 * A port of the legacy `content/our-technologies/cover` tastic, which is what
 * https://methven-stage.gwabk.com.au/nz/our-technologies/ still serves. Every number below was
 * measured off that page at 479/480/640/768/900/1023/1024/1280/1440/1660/1920, so this renders
 * identically to it rather than approximately.
 *
 * THREE COLUMN COUNTS, and they are the repo's own breakpoints, not Tailwind's defaults
 * (tailwind.config.js overrides `screens`: sm 480, md 768, lg 1024):
 *
 *   < 480px        grid-cols-1      one tile per row
 *   480 – 1023px   sm:grid-cols-2   two per row
 *   >= 1024px      lg:grid-cols-4   four per row
 *
 * Verified against stage: gridTemplateColumns is a single track at 479 and two at 480; two at
 * 1023 and four at 1024. `md` (768) is deliberately NOT a step — the legacy markup skips it.
 *
 * THE GRID IS FULL BLEED. The legacy wrapper carries no horizontal padding at any width, so the
 * first tile starts at x=0 and the last ends flush with the viewport (confirmed at all 13 widths
 * above: grid box is always `x: 0, w: viewport`). That is why this rendering does not use the
 * `px-24 md:px-28 lg:px-48 …` page gutter the other renderings do — adding it would be a visible
 * change, not a fix. The optional section title/description DO take the gutter, since they are
 * text and would otherwise touch the edge; the live page has neither, so nothing moves today.
 *
 * `gap-10` is 10px, not 40px. tailwind.config.js replaces the whole `spacing` scale with 1–400px,
 * so every spacing utility here (`gap-10`, `p-2`, `p-5`, `mt-2`, `translate-x-4`, `w-32`) is that
 * many PIXELS. Reading them as Tailwind defaults gives numbers 4x too large.
 *
 * Images keep their intrinsic aspect ratio (`width: 100%, height: auto`) exactly as the legacy
 * `<img className="w-full h-auto">` does — tile heights are equalised by the grid stretching them,
 * not by cropping, so a card whose image has a different aspect stays uncropped.
 *
 * NOT ported: the legacy 150-character description truncation. It runs `new DOMParser()`, which
 * does not exist on the server and would break the SSR pass for the whole page (the same failure
 * mode as the dompurify incident in helpers/rendering.tsx). It is also redundant — the migrated
 * card descriptions are already stored truncated, with their trailing ellipsis, so re-truncating
 * would clip the ellipsis off. Authors control the length in the CMS.
 */
export default function QuasarRendering({ content }: { content: GenericSectionContent }) {
  const { title, description, content_alignment, cards = [], additional_css, additional_css_class_names } = content;
  const { uniqueClass, scopedAdditionalCss } = useAdditionalCss(additional_css, 'QuasarRendering');
  const align = textAlignmentClass(content_alignment);

  // Only the heading block takes the page gutter; the grid below stays edge to edge.
  const headingPadX = 'px-24 md:px-28 lg:px-48 2xl:px-80 3xl:px-0';

  return (
    <>
      {scopedAdditionalCss && <style dangerouslySetInnerHTML={{ __html: scopedAdditionalCss }} />}
      <div className={`${SECTION_SPACING} w-full ${[additional_css_class_names, uniqueClass].filter(Boolean).join(' ')}`}>
        {title && (
          <Typography
            as="h2"
            className={`${headingPadX} mb-32 text-left text-32 font-medium leading-10 text-[#090909] lg:mb-40 xl:text-36 2xl:mb-48 2xl:text-48`}
          >
            {title}
          </Typography>
        )}
        {description && (
          <div
            className={`${headingPadX} mb-24 text-16 font-normal leading-8 text-[#333] 2xl:text-20 ${align}`}
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(description) }}
          />
        )}
        <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-4">
          {cards.map((card, index) => (
            /*
              Background #F3EDE9 and the hover shadow sit on this wrapper, not on the anchor, so the
              tint fills the whole grid cell once the grid stretches short tiles to the row height.
            */
            <div key={index} className="block bg-[#F3EDE9] transition-shadow hover:shadow-lg">
              <Link link={card?.ctas?.[0]?.url ?? '#'} className="group block">
                {card?.desktop_image?.url && (
                  <Image
                    priority
                    loading="eager"
                    src={encodeURI(card.desktop_image.url)}
                    alt={card.desktop_image.description ?? card?.title ?? ''}
                    sizes="(min-width: 1024px) 25vw, (min-width: 480px) 50vw, 100vw"
                    style={{ width: '100%', height: 'auto' }}
                  />
                )}
                {card?.title && (
                  /*
                    18px / 28px / 600, centred, gray-800 — measured off stage, where the title is an
                    <h1> emitted by the legacy Markdown component and centred by its stylesheet. It
                    is an <h3> here because one <h1> per card is wrong on a page that already has
                    one; the computed type is identical.

                    NationalBook at weight 600, NOT NationalSemibold. NationalBook ships a single
                    400 face, so the browser synthesises the bold — which is exactly what stage
                    renders. Swapping in the real semibold face would be a different look.

                    The `!` on font-semibold is load-bearing. next/font emits a class that sets
                    font-family AND `font-weight: 400` — the one weight NationalBook declares — and
                    that class lands after Tailwind's utilities, so plain `font-semibold` lost and
                    the title rendered at 400. It was the only measurable difference from stage.
                    The legacy page never hits this: it applies no font class and just inherits.
                  */
                  <div className="mt-2 p-2">
                    <Typography
                      as="h3"
                      className={`text-center text-lg !font-semibold text-gray-800 ${NationalBook.className}`}
                    >
                      {card.title}
                    </Typography>
                  </div>
                )}
                {card?.description && (
                  <div className={`p-2 ${card?.title ? '' : 'mt-2'}`}>
                    <div
                      className={`p-5 text-sm text-gray-600 ${NationalBook.className}`}
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(card.description) }}
                    />
                  </div>
                )}
                {/*
                  The arrow sits at the LEFT edge despite the legacy `text-right`, because Tailwind's
                  preflight makes <svg> display:block and text-align cannot move a block. Stage
                  renders it at x=2 (the 2px padding) — left — so `text-right` is dropped here
                  rather than reproduced, to stop it reading as an intended right alignment.

                  It follows the text rather than being pinned to the bottom of the tile, so tiles
                  in one row can have their arrows at different heights. That is stage's behaviour.
                */}
                <div className="mt-2 p-2 transition-transform duration-300 group-hover:translate-x-4">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24" className="h-32 w-32 text-gray-600">
                    <path
                      fillRule="evenodd"
                      d="M13.293 4.293a1 1 0 011.414 0l6 6a1 1 0 01.083 1.32l-.083.094-6 6a1 1 0 01-1.497-1.32l.083-.094L17.585 12H4a1 1 0 01-.117-1.993L4 10h13.585l-4.293-4.293a1 1 0 01-.083-1.32z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </Link>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
